import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  User,
  Building2,
  BookOpen,
} from "lucide-react";
import { examAssignmentsAPI } from "@/services/api";
import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  addWeeks,
  subWeeks,
  parseISO,
  getHours,
  getMinutes,
} from "date-fns";

const WeeklyCalendarView = ({ scheduleFilter = "all" }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Get the week range
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 }); // Sunday
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
  const daysInWeek = eachDayOfInterval({ start: weekStart, end: weekEnd });

  // Get exam assignments for the current week
  const { data, isLoading } = useQuery({
    queryKey: ["exam-assignments-weekly", format(weekStart, "yyyy-MM-dd")],
    queryFn: () =>
      examAssignmentsAPI.getAll({
        limit: 1000,
        search: "",
      }),
  });

  // Extended time slots (7 AM to 12 AM - 24 hours)
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 7; hour <= 23; hour++) {
      slots.push({
        hour,
        label: hour === 12 ? "12 PM" : hour > 12 ? `${hour - 12} PM` : `${hour} AM`,
        shortLabel: hour > 12 ? `${hour - 12}PM` : `${hour}AM`,
      });
    }
    // Add midnight (12 AM)
    slots.push({
      hour: 0,
      label: "12 AM",
      shortLabel: "12AM",
    });
    return slots;
  }, []);

  // Organize assignments by date and time with filtering
  const assignmentsByDateTime = useMemo(() => {
    const organized = {};

    if (data?.data) {
      let allAssignments = [];
      
      // Apply filtering
      switch (scheduleFilter) {
        case "my":
          allAssignments = data.data.myAssignments || [];
          break;
        case "company":
          allAssignments = data.data.otherAssignments || [];
          break;
        default: // "all"
          allAssignments = [
            ...(data.data.myAssignments || []),
            ...(data.data.otherAssignments || []),
          ];
          break;
      }

      allAssignments.forEach((assignment) => {
        if (assignment.dateOfExam) {
          const examDate = parseISO(assignment.dateOfExam);
          const dateKey = format(examDate, "yyyy-MM-dd");
          const hour = getHours(examDate);

          if (!organized[dateKey]) {
            organized[dateKey] = {};
          }
          if (!organized[dateKey][hour]) {
            organized[dateKey][hour] = [];
          }

          organized[dateKey][hour].push({
            ...assignment,
            examDate,
            isMyAssignment: data.data.myAssignments?.some(
              (my) => my._id === assignment._id
            ),
          });
        }
      });
    }

    return organized;
  }, [data, scheduleFilter]);

  const navigateWeek = (direction) => {
    if (direction === "prev") {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  // ... keep existing code (getAssignmentColor, formatTime, getStatusColor functions)

  const getAssignmentColor = (assignment) => {
    if (assignment.isMyAssignment) {
      switch (assignment.status) {
        case "pending":
          return "bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-700";
        case "ongoing":
          return "bg-gradient-to-r from-yellow-500 to-yellow-600 text-white border-yellow-700";
        case "completed":
          return "bg-gradient-to-r from-green-500 to-green-600 text-white border-green-700";
        case "disqualified":
          return "bg-gradient-to-r from-red-500 to-red-600 text-white border-red-700";
        default:
          return "bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-700";
      }
    } else {
      switch (assignment.status) {
        case "pending":
          return "bg-gradient-to-r from-purple-400 to-purple-500 text-white border-purple-600";
        case "ongoing":
          return "bg-gradient-to-r from-orange-400 to-orange-500 text-white border-orange-600";
        case "completed":
          return "bg-gradient-to-r from-teal-400 to-teal-500 text-white border-teal-600";
        case "disqualified":
          return "bg-gradient-to-r from-pink-400 to-pink-500 text-white border-pink-600";
        default:
          return "bg-gradient-to-r from-purple-400 to-purple-500 text-white border-purple-600";
      }
    }
  };

  const formatTime = (examDate) => {
    return format(examDate, "HH:mm");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "ongoing":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "completed":
        return "bg-emerald-100 text-emerald-800 border-emerald-200";
      case "disqualified":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="h-full">
        <Card className="h-full flex flex-col">
          <CardHeader className="pb-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-b flex-shrink-0">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-blue-600" />
                {format(weekStart, "MMM d")} - {format(weekEnd, "MMM d, yyyy")}
              </CardTitle>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateWeek("prev")}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-3 w-3" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentDate(new Date())}
                  className="h-8 px-2 text-xs"
                >
                  Today
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateWeek("next")}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-hidden">
            <div className="h-full flex flex-col">
              {/* Header with days */}
              <div className="grid grid-cols-8 border-b bg-gray-50 flex-shrink-0">
                <div className="p-2 text-center font-semibold text-gray-600 border-r bg-white">
                  <Clock className="h-3 w-3 mx-auto mb-1" />
                  <span className="text-xs">Time</span>
                </div>
                {daysInWeek.map((day) => {
                  const isToday = isSameDay(day, new Date());
                  return (
                    <div
                      key={day.toISOString()}
                      className={`p-2 text-center font-semibold border-r transition-colors ${
                        isToday 
                          ? "bg-blue-100 text-blue-700 border-blue-200" 
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <div className="text-xs font-medium">{format(day, "EEE")}</div>
                      <div className={`text-sm font-bold ${isToday ? "text-blue-600" : ""}`}>
                        {format(day, "d")}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Time slots and events - scrollable */}
              <div className="flex-1 overflow-y-auto">
                {timeSlots.map((timeSlot) => (
                  <div
                    key={timeSlot.hour}
                    className="grid grid-cols-8 border-b hover:bg-gray-50 transition-colors"
                    style={{ minHeight: '50px' }}
                  >
                    <div className="p-1 text-xs text-gray-500 border-r bg-gray-50/50 flex items-start justify-center">
                      <span className="font-medium">{timeSlot.shortLabel}</span>
                    </div>
                    {daysInWeek.map((day) => {
                      const dateKey = format(day, "yyyy-MM-dd");
                      const assignments =
                        assignmentsByDateTime[dateKey]?.[timeSlot.hour] || [];

                      return (
                        <div
                          key={`${day.toISOString()}-${timeSlot.hour}`}
                          className="p-1 border-r relative bg-white hover:bg-gray-50 transition-colors"
                        >
                          <div className="space-y-0.5">
                            {assignments.map((assignment, index) => (
                              <Tooltip key={assignment._id}>
                                <TooltipTrigger asChild>
                                  <div
                                    className={`${getAssignmentColor(assignment)} 
                                      p-1 rounded cursor-pointer shadow-sm hover:shadow-md 
                                      transition-all duration-200 border-l-2 transform hover:scale-105`}
                                  >
                                    <div className="flex items-center gap-1 mb-0.5">
                                      <Clock className="h-2 w-2" />
                                      <span className="text-xs font-semibold">
                                        {formatTime(assignment.examDate)}
                                      </span>
                                    </div>
                                    <div className="font-medium text-xs truncate">
                                      {assignment.candidateId?.name}
                                    </div>
                                    <div className="text-xs opacity-90 truncate">
                                      {assignment.examId?.name}
                                    </div>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                      <User className="h-4 w-4 text-blue-500" />
                                      <span className="font-semibold">
                                        {assignment.candidateId?.name}
                                      </span>
                                      {assignment.isMyAssignment && (
                                        <Badge variant="secondary" className="text-xs">
                                          Your Assignment
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <BookOpen className="h-4 w-4 text-green-500" />
                                      <span>{assignment.examId?.name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Building2 className="h-4 w-4 text-purple-500" />
                                      <span>{assignment.companyId?.name || 'Company'}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Clock className="h-4 w-4 text-orange-500" />
                                      <span>
                                        {format(assignment.examDate, "MMM d, yyyy 'at' HH:mm")}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge className={getStatusColor(assignment.status)}>
                                        {assignment.status.toUpperCase()}
                                      </Badge>
                                    </div>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};

export default WeeklyCalendarView;